import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Loader2, FileText, CheckCircle, Clock,
  XCircle, Eye, ArrowRight, Filter, Trash2, MessageSquare, Mail
} from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const STATUT_CONFIG = {
  brouillon:        { label: 'Brouillon',        color: 'bg-slate-100 text-slate-600',   icon: FileText },
  soumise:          { label: 'Soumise',           color: 'bg-blue-100 text-blue-700',     icon: Clock },
  analyse_en_cours: { label: 'Analyse IA...',     color: 'bg-amber-100 text-amber-700',   icon: Clock },
  analysee:         { label: 'Analysee',          color: 'bg-blue-100 text-blue-700',     icon: Eye },
  validee:          { label: 'Validee',           color: 'bg-emerald-100 text-emerald-700',icon: CheckCircle },
  convertie:        { label: 'Convertie',         color: 'bg-emerald-100 text-emerald-700',icon: ArrowRight },
  rejetee:          { label: 'Rejetee',           color: 'bg-red-100 text-red-600',       icon: XCircle },
}

const SOURCE_LABELS = {
  chat_client: { label: 'Client', icon: MessageSquare, color: 'text-blue-600 bg-blue-50' },
  email_gestionnaire: { label: 'Gestionnaire', icon: Mail, color: 'text-indigo-600 bg-indigo-50' },
}

export default function PreOuverturesList({ tenants, profile }) {
  const navigate = useNavigate()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [tenantFilter, setTenantFilter] = useState('')
  const [statutFilter, setStatutFilter] = useState('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      // Fetch V4 pre-declarations
      const params = new URLSearchParams()
      if (tenantFilter) params.set('tenant_id', tenantFilter)
      if (statutFilter) params.set('statut', statutFilter)
      // Filtrer par gestionnaire sauf pour admin/master
      if (profile?.id && profile?.role !== 'master_admin' && profile?.role !== 'c2_admin') {
        params.set('user_id', profile.id)
        params.set('role', profile.role)
      }
      const r = await fetch(`${API}/api/pre-declarations-v4?${params}`)
      const d = await r.json()
      const v4 = (d.pre_declarations || []).map(pd => ({
        ...pd,
        _type: 'v4',
      }))

      // Also fetch V3 pre-ouvertures (legacy)
      let v3 = []
      try {
        const params3 = new URLSearchParams({ limit: '50' })
        if (tenantFilter) params3.set('tenant_id', tenantFilter)
        if (statutFilter) params3.set('statut', statutFilter)
        const r3 = await fetch(`${API}/api/pre-ouvertures?${params3}`)
        const d3 = await r3.json()
        v3 = (d3.pre_ouvertures || []).map(po => ({
          ...po,
          _type: 'v3',
        }))
      } catch { /* V3 may not exist */ }

      // Merge and sort by date
      const all = [...v4, ...v3].sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )
      setItems(all)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [tenantFilter, statutFilter])

  useEffect(() => { fetchData() }, [fetchData])

  const handleDelete = async (e, item) => {
    e.stopPropagation()
    const isV4 = item._type === 'v4'
    const endpoint = isV4 ? `pre-declarations/${item.id}` : `pre-ouvertures/${item.id}`
    const label = isV4 ? 'pre-declaration' : 'pre-ouverture'
    if (!window.confirm(`Supprimer cette ${label} ?`)) return
    try {
      const r = await fetch(`${API}/api/${endpoint}`, { method: 'DELETE' })
      if (r.ok) {
        toast.success('Supprime')
        fetchData()
      } else {
        toast.error('Erreur suppression')
      }
    } catch {
      toast.error('Erreur reseau')
    }
  }

  const getTenantName = (tid) => {
    const t = (tenants || []).find(t => t.id === tid)
    return t?.nom || tid?.substring(0, 8) + '...'
  }

  const fmtDate = (d) => {
    if (!d) return '--'
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const getDescription = (item) => {
    if (item._type === 'v4') {
      const data = item.donnees_structurees || {}
      if (data.description) return data.description.substring(0, 100)
      if (data.nature_sinistre) return data.nature_sinistre
      const msgs = item.messages_chat || []
      const firstUser = msgs.find(m => m.role === 'user')
      if (firstUser) return firstUser.content?.substring(0, 100)
      return '(Pre-declaration IA)'
    }
    return item.contenu_mail?.substring(0, 100) || '--'
  }

  const getRowUrl = (item) => {
    if (item._type === 'v4') {
      if (item.statut === 'convertie' && item.sinistre_id) return `/c2/sinistre/${item.sinistre_id}`
      return `/c2/pre-declarations/${item.id}`
    }
    if (item.statut === 'validee' && item.sinistre_id) return `/c2/sinistres/${item.sinistre_id}`
    return `/c2/pre-ouvertures/${item.id}`
  }

  return (
    <div className="space-y-5" data-testid="pre-declarations-list">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Pre-declarations</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {items.length} pre-declaration(s) — Declarations clients et gestionnaires
          </p>
        </div>
        <button
          onClick={() => navigate('/c2/pre-declarations/new')}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
          data-testid="new-pre-declaration-btn"
        >
          <Plus size={16} /> Nouvelle pre-declaration
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3">
        <Filter size={16} className="text-slate-400" />
        <select
          value={tenantFilter}
          onChange={e => setTenantFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
          data-testid="filter-tenant"
        >
          <option value="">Tous les clients</option>
          {(tenants || []).map(t => (
            <option key={t.id} value={t.id}>{t.nom}</option>
          ))}
        </select>
        <select
          value={statutFilter}
          onChange={e => setStatutFilter(e.target.value)}
          className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
          data-testid="filter-statut"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(STATUT_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Aucune pre-declaration</p>
          <p className="text-xs text-slate-400 mt-1">
            Les declarations clients et les pre-declarations gestionnaires apparaitront ici.
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map(item => {
                const cfg = STATUT_CONFIG[item.statut] || STATUT_CONFIG.brouillon
                const Icon = cfg.icon
                const src = SOURCE_LABELS[item.source] || SOURCE_LABELS.email_gestionnaire
                const SrcIcon = src?.icon || Mail
                return (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(getRowUrl(item))}
                    data-testid={`predecl-row-${item.id}`}
                  >
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                      {fmtDate(item.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md font-medium ${src?.color || ''}`}>
                        <SrcIcon size={11} />
                        {item._type === 'v3' ? 'V3' : src?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium text-xs">
                      {item.tenants?.nom || getTenantName(item.tenant_id)}
                    </td>
                    <td className="px-4 py-3 text-slate-500 max-w-xs truncate text-xs">
                      {getDescription(item)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                        <Icon size={12} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(item.statut === 'convertie' || item.statut === 'validee') && item.sinistre_id ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                          <ArrowRight size={14} /> Sinistre
                        </span>
                      ) : item.statut === 'soumise' ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                          <Eye size={14} /> Traiter
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-indigo-600 font-medium">
                          <Eye size={14} /> Voir
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={e => handleDelete(e, item)}
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors group"
                        title="Supprimer"
                        data-testid={`predecl-delete-${item.id}`}
                      >
                        <Trash2 size={14} className="text-slate-300 group-hover:text-red-500" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
